import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type { ApplicationCode } from '@shared/config/applications';
import {
  EMPTY_AUTHORIZATION,
  checkPermission,
  hasApplication,
  hasAuthorizationPayload,
  resolveAuthorization,
  resolveScope,
  toPermissionSet,
} from '@shared/lib/authorization';
import {
  findAccount,
  readAccounts,
  readActiveAccountId,
  removeAccount as removeAccountFrom,
  toSavedAccount,
  upsertAccount,
  writeAccounts,
  writeActiveAccountId,
} from '@shared/lib/accounts';
import type {
  AuthorizationData,
  AuthSession,
  AuthTokens,
  AuthUser,
  PermissionScope,
  SavedAccount,
} from '@shared/types/auth';

/**
 * Authentication + authorization state.
 *
 * Security posture: only the short-lived access token is kept, in memory. The
 * refresh token never touches JS — it is an HttpOnly cookie owned by the
 * backend. Nothing here is persisted to storage, so a page reload relies on the
 * cookie-driven `/auth/refresh` + `/auth/me` bootstrap (see `AuthProvider`).
 *
 * Authorization (`isOwner` / `permissions` / `scopes`) is derived from the same
 * backend payload that carries the user, so there is a single source of truth
 * and no screen ever fetches permissions on its own. It is cleared with the
 * session on logout.
 */
export interface AuthorizationState extends AuthorizationData {
  /** Indexed permissions for O(1) checks. */
  permissionSet: ReadonlySet<string>;
  /** True once an authorization payload has been applied at least once. */
  loaded: boolean;
}

const emptyAuthorization: AuthorizationState = {
  ...EMPTY_AUTHORIZATION,
  permissionSet: toPermissionSet([]),
  loaded: false,
};

function toAuthorizationState(
  source: AuthUser | AuthSession | null | undefined,
  fallback?: AuthUser | AuthSession | null,
): AuthorizationState {
  const data = resolveAuthorization(source, fallback);
  return { ...data, permissionSet: toPermissionSet(data.permissions), loaded: true };
}

/**
 * Why the last session restore left this tab signed out, when that is worth
 * saying. `ended`: identity-service refused a session this tab expected to
 * continue (handed over by another application, or the one it last used) —
 * the sign-in page explains it. `unavailable`: the restore could not be
 * answered (identity unreachable, 5xx, 429) — the session may still be valid,
 * so a retry is offered instead of a sign-in form. `null`: nothing to say.
 */
export type SessionRestoreFailure = 'ended' | 'unavailable' | null;

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  /** Accounts signed in on this browser (metadata only). */
  accounts: SavedAccount[];
  /** `user.id` of the account whose session is live. */
  activeAccountId: string | null;
  /** Server session backing the active account, sent as `X-Session-Id`. */
  activeSessionId: string | null;
  /** Effective authorization for the current user. */
  authorization: AuthorizationState;
  /** True until the initial session-restore attempt completes. */
  isInitializing: boolean;
  /** Outcome of a failed restore, read by the route guard. */
  sessionRestore: SessionRestoreFailure;

  // Derived
  isAuthenticated: boolean;
  /** Role names are display-only; authorize with `can` / `canAny` / `canAll`. */
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: readonly string[]) => boolean;
  can: (permission: string) => boolean;
  canAny: (permissions: readonly string[]) => boolean;
  canAll: (permissions: readonly string[]) => boolean;
  scopeFor: (permission: string) => PermissionScope | null;
  /** Application access (data on the account; no Owner bypass). */
  hasApplication: (application: ApplicationCode) => boolean;

  // Accounts
  /** Remember (or refresh) an account and make it the active one. */
  rememberAccount: (user: AuthUser, session?: AuthSession) => void;
  /** Forget an account; returns the next account to activate, if any. */
  forgetAccount: (userId: string) => SavedAccount | null;
  setActiveAccount: (userId: string | null, sessionId?: string | null) => void;

  // Mutations
  setSession: (user: AuthUser, tokens: AuthTokens, session?: AuthSession) => void;
  setUser: (user: AuthUser) => void;
  setTokens: (tokens: AuthTokens) => void;
  setInitializing: (value: boolean) => void;
  setSessionRestore: (value: SessionRestoreFailure) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>()(
  subscribeWithSelector((set, get) => ({
    user: null,
    accessToken: null,
    // Restored synchronously so the first render already knows which accounts
    // exist and which one to bring back. The session id is read from the saved
    // account, not from memory — after a reload there is no memory, and the
    // bootstrap refresh needs it to name the session it is restoring.
    accounts: readAccounts(),
    activeAccountId: readActiveAccountId(),
    activeSessionId: findAccount(readAccounts(), readActiveAccountId())?.sessionId ?? null,
    authorization: emptyAuthorization,
    isInitializing: true,
    sessionRestore: null,

    isAuthenticated: false,

    hasRole: (role) => get().user?.roles?.includes(role) ?? false,

    hasAnyRole: (roles) => roles.some((role) => get().hasRole(role)),

    can: (permission) => {
      const { authorization } = get();
      return checkPermission(authorization, authorization.permissionSet, permission);
    },

    canAny: (permissions) => permissions.some((permission) => get().can(permission)),

    canAll: (permissions) => permissions.every((permission) => get().can(permission)),

    scopeFor: (permission) => resolveScope(get().authorization, permission),

    hasApplication: (application) => hasApplication(get().authorization, application),

    /**
     * Apply a session payload.
     *
     * Authorization is only replaced when the payload actually carries it.
     * A login or cookie-refresh response may return just the user identity,
     * and overwriting with an empty set there would strip an owner of every
     * affordance mid-session (and on every page reload). Absent ≠ empty.
     */
    setSession: (user, tokens, session) =>
      set({
        user,
        accessToken: tokens.accessToken,
        isAuthenticated: true,
        sessionRestore: null,
        authorization:
          hasAuthorizationPayload(user) || hasAuthorizationPayload(session)
            ? toAuthorizationState(user, session)
            : get().authorization,
      }),

    setUser: (user) =>
      set({
        user,
        isAuthenticated: Boolean(get().accessToken),
        authorization: hasAuthorizationPayload(user)
          ? toAuthorizationState(user)
          : get().authorization,
      }),

    setTokens: (tokens) => set({ accessToken: tokens.accessToken }),

    setInitializing: (value) => set({ isInitializing: value }),

    setSessionRestore: (value) => set({ sessionRestore: value }),

    rememberAccount: (user, session) => {
      const { accounts } = get();
      const account = toSavedAccount(user, session, findAccount(accounts, user.id) ?? undefined);
      const next = upsertAccount(accounts, account);
      writeAccounts(next);
      writeActiveAccountId(account.userId);
      set({
        accounts: next,
        activeAccountId: account.userId,
        activeSessionId: account.sessionId,
      });
    },

    forgetAccount: (userId) => {
      const next = removeAccountFrom(get().accounts, userId);
      writeAccounts(next);
      const wasActive = get().activeAccountId === userId;
      const successor = wasActive ? (next[0] ?? null) : null;
      if (wasActive) writeActiveAccountId(successor?.userId ?? null);
      set({
        accounts: next,
        ...(wasActive ? { activeAccountId: successor?.userId ?? null, activeSessionId: null } : {}),
      });
      return successor;
    },

    setActiveAccount: (userId, sessionId) => {
      writeActiveAccountId(userId);
      set({
        activeAccountId: userId,
        activeSessionId: sessionId ?? findAccount(get().accounts, userId)?.sessionId ?? null,
      });
    },

    /**
     * Drop the live session. Saved accounts are deliberately kept — signing one
     * account out must never sign the others out.
     */
    clear: () =>
      set({
        user: null,
        accessToken: null,
        isAuthenticated: false,
        activeSessionId: null,
        authorization: emptyAuthorization,
        sessionRestore: null,
      }),
  })),
);

/** Non-React accessor for the current access token (used by the API layer). */
export const getAccessToken = (): string | null => useAuthStore.getState().accessToken;

/** Session id of the active account, sent as `X-Session-Id` by the API layer. */
export const getActiveSessionId = (): string | null => useAuthStore.getState().activeSessionId;

/** Non-React authorization accessor for imperative code (router helpers, services). */
export const getAuthorization = (): AuthorizationState => useAuthStore.getState().authorization;

/** Non-React permission check for imperative code. */
export const can = (permission: string): boolean => useAuthStore.getState().can(permission);
